<template>
  <div ref="formContainer">
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div v-if="docs" class="print-content">
        <!-- Basic Information -->
        <!-- <v-card-title>基本資料</v-card-title> -->
        <v-row dense>
          <v-col cols="12" md="auto">
            <ValidationProvider v-slot="{ errors }" name="登記日期" rules="required">
              <DateInputSeparate
                v-model="inputDate"
                label="登記日期 *"
                :default-year="currentYear"
                :error="!!errors.length"
                :error-messages="errors"
                :error-details-class="dateFieldErrorDetailsClass"
                dense
                flat
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="auto">
            <ValidationProvider v-slot="{ errors }" name="投保地點">
              <v-autocomplete
                v-model="docs.insPlace"
                :error="!!errors.length"
                :error-messages="errors"
                :items="insPlaceOptions"
                flat
                label="投保地點"
                outlined
                dense
                hide-details="auto"
                style="max-width: 150px"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="auto">
            <ValidationProvider v-slot="{ errors }" name="客戶名稱" rules="required">
              <SearchableCombobox
                v-model="docs.ContactPerson"
                path="v1/autocomplete-profiles"
                label="客戶名稱 *"
                :error="!!errors.length"
                :error-messages="errors"
                :params="{ filter: { type: 'client' }, searchPaths: ['name'] }"
                item-text="name"
                dense
                @selectItem="handleCustomerSelect"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{
                      item.phone
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </SearchableCombobox>
            </ValidationProvider>
          </v-col>

          <v-col cols="12" md="auto">
            <ValidationProvider v-slot="{ errors }" name="保險種類" rules="required">
              <v-autocomplete
                v-model="docs.insType"
                :error="!!errors.length"
                :error-messages="errors"
                :items="insuranceTypeOptions"
                flat
                label="保險種類 *"
                outlined
                dense
                hide-details="auto"
                style="max-width: 150px"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="auto">
            <ValidationProvider v-slot="{ errors }" name="檔案編號" rules="required">
              <v-text-field
                v-model="docs.N1"
                :error="!!errors.length || n1Error"
                :error-messages="errors.length ? errors : n1Error ? [n1ErrorMessage] : []"
                :loading="checkingN1"
                flat
                label="檔案編號 *"
                outlined
                dense
                hide-details="auto"
                @blur="checkN1Duplicate"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Original Information -->
        <v-subheader class="px-0">原始信息資料</v-subheader>
        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="車牌號碼">
              <v-text-field
                v-model="docs.data[0].VEHNO"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="車牌號碼"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="保險公司">
              <v-autocomplete
                v-model="docs.NAMEOFINS"
                :error="!!errors.length"
                :error-messages="errors"
                :items="insuranceCompanyOptions"
                flat
                label="保險公司"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="廠商">
              <v-text-field
                v-model="docs.data[0].MAKE"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="廠商"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="型號">
              <v-text-field
                v-model="docs.data[0].MODEL"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="型號"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="年份">
              <v-text-field
                v-model="docs.data[0].YEAR"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="年份"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="NCD">
              <v-text-field
                v-model="docs.D1"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="NCD"
                outlined
                dense
                hide-details="auto"
                append-icon="mdi-percent"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Policy Details -->
        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="臨時保單號碼">
              <v-text-field
                v-model="docs.data[0].CNNO"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="臨時保單號碼"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="代理">
              <v-autocomplete
                v-model="docs.AGENCY"
                :error="!!errors.length"
                :error-messages="errors"
                :items="agencyOptions"
                flat
                label="代理"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="電話">
              <v-text-field
                v-model="docs.TEL"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="電話"
                outlined
                dense
                hide-details="auto"
                autocomplete="off"
                inputmode="tel"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="代理人">
              <SearchableCombobox
                v-model="docs.agent"
                path="v1/autocomplete-profiles"
                label="代理人"
                :error="!!errors.length"
                :error-messages="errors"
                :params="{ filter: { type: 'agent' }, searchPaths: ['name'] }"
                item-text="name"
                dense
                @selectItem="handleAgentSelect"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{
                      item.phone
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </SearchableCombobox>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="代理人電話">
              <SearchableCombobox
                v-model="docs.agentTel"
                path="v1/autocomplete-profiles"
                label="代理人電話"
                :error="!!errors.length"
                :error-messages="errors"
                :params="{ filter: { type: 'agent' }, searchPaths: ['phone'] }"
                item-text="phone"
                dense
                @selectItem="handleAgentPhoneSelect"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.phone }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{
                      item.name
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </SearchableCombobox>
            </ValidationProvider>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="正式保單號碼">
              <v-text-field
                v-model="docs.data[0].POLICYNO"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="正式保單號碼"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4" lg="3" xl="2" sm="6">
            <ValidationProvider v-slot="{ errors }" name="保費">
              <v-text-field
                v-model="docs.data[0].PREMIUM"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="保費"
                outlined
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Dates, attachment, pending files -->
        <v-row dense align="start">
          <v-col cols="12" md="auto">
            <ValidationProvider v-slot="{ errors }" vid="起保日期" name="起保日期" rules="required">
              <DateInputSeparate
                v-model="businessDate"
                label="起保日期 *"
                :default-year="currentYear"
                :error="!!errors.length"
                :error-messages="errors"
                :error-details-class="dateFieldErrorDetailsClass"
                dense
                flat
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="auto">
            <ValidationProvider
              v-slot="{ errors }"
              name="到期日"
              :rules="endDateRules"
            >
              <DateInputSeparate
                ref="endDateInput"
                v-model="endDate"
                label="到期日 *"
                :default-year="currentYear"
                :error="!!errors.length"
                :value-error="isNonStandardDateSpan"
                :error-messages="errors"
                :error-details-class="dateFieldErrorDetailsClass"
                dense
                flat
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" sm="6" md="auto">
            <ValidationProvider v-slot="{ errors }" name="附件" rules="required">
              <label class="v-label">附件 *</label>
              <v-radio-group
                v-model="docs.data[0].depPlace"
                :error="!!errors.length"
                :error-messages="errors"
                row
                dense
                hide-details="auto"
                class="mt-0"
              >
                <v-radio
                  v-for="option in depPlaceOptions"
                  :key="option"
                  :value="option"
                  :label="option"
                ></v-radio>
              </v-radio-group>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" sm="6" md="auto">
            <ValidationProvider v-slot="{ errors }" name="待補文件">
              <label class="v-label">待補文件</label>
              <div class="d-flex gap-4">
                <v-checkbox
                  v-for="option in needFileOptions"
                  :key="option.value"
                  v-model="docs.needFile"
                  :value="option.value"
                  :label="option.text"
                  hide-details
                  dense
                  class="mt-0"
                ></v-checkbox>
              </div>
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Remark -->
        <v-row dense>
          <v-col cols="12">
            <ValidationProvider v-slot="{ errors }" name="備註說明">
              <v-textarea
                v-model="docs.data[0].REMARK"
                :error="!!errors.length"
                :error-messages="errors"
                flat
                label="備註說明"
                outlined
                rows="3"
                dense
                hide-details="auto"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Update Data Items Section -->
        <v-row v-if="!isNewRecord && docs.data && docs.data.length > 1" dense>
          <v-col cols="12">
            <div
              v-for="(item, index) in docs.data.slice(1)"
              :key="'data-item-' + index"
              class="mb-4"
            >
              <v-card outlined>
                <v-card-title class="py-2 px-3">
                  <span class="text-subtitle-2">保險批單 {{ formatDate(item.inputDate) }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon small class="no-print" @click="editDataItem(index + 1)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    class="no-print"
                    @click="deleteDataItem(index + 1)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text class="pa-3">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td>
                            <div class="text-caption grey--text">車牌號碼</div>
                            <div>{{ item.VEHNO || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">廠商</div>
                            <div>{{ item.MAKE || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">型號</div>
                            <div>{{ item.MODEL || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">年份</div>
                            <div>{{ item.YEAR || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">臨時保單號碼</div>
                            <div>{{ item.CNNO || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">保費</div>
                            <div>{{ item.PREMIUM || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">正式保單號碼</div>
                            <div>{{ item.POLICYNO || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">附件</div>
                            <div>{{ item.depPlace || '-' }}</div>
                          </td>
                          <td>
                            <div class="text-caption grey--text">備註說明</div>
                            <div>{{ item.REMARK || '-' }}</div>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </div>
          </v-col>
        </v-row>

        <!-- Attachments Section -->
        <v-row dense>
          <v-col cols="12">
            <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
              <label class="v-label">附件檔案</label>
              <v-btn class="no-print" small color="primary" @click="$refs.attachmentInput.click()">
                <v-icon small left>mdi-upload</v-icon>
                上傳檔案
              </v-btn>
            </div>

            <!-- Attachments Table -->
            <v-simple-table v-if="docs.attachments && docs.attachments.length > 0" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">檔案名稱</th>
                    <th class="text-left">訊息傳送時間</th>
                    <th class="text-center" width="100">查看</th>
                    <th class="text-center" width="100">刪除</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(attachment, index) in docs.attachments" :key="'attachment-' + index">
                    <td>
                      <v-icon small left>mdi-file-document</v-icon>
                      {{ attachment.filename || '文件' }}
                    </td>
                    <td class="text-caption grey--text text--darken-1">
                      {{ getAttachmentLastSentAt(attachment) }}
                    </td>
                    <td class="text-center">
                      <v-btn icon small @click="downloadAttachment(attachment)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </td>
                    <td class="text-center">
                      <v-btn icon small color="error" @click="removeAttachment(index)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-alert v-else type="info" outlined dense class="mt-2"> 尚未上傳任何附件 </v-alert>

            <!-- Hidden file input -->
            <input
              ref="attachmentInput"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              style="display: none"
              @change="handleAttachmentSelect"
              multiple
            />

            <v-switch
              v-model="autoForwardToClient"
              class="no-print mt-3 mb-0"
              dense
              hide-details
              :disabled="!hasWhatsappRecipientPhone"
              label="自動轉發給客戶"
            />
            <div
              v-if="!hasWhatsappRecipientPhone"
              class="text-caption grey--text no-print mb-1"
            >
              {{ autoForwardToClientDisabledReason }}
            </div>
          </v-col>
        </v-row>

        <!-- Action Buttons -->
        <v-row dense class="mt-4 no-print">
          <v-col class="d-flex align-center gap-2 no-print">
            <v-btn
              v-if="showAbandonPolicyButton"
              :disabled="invalid || isMaster || saving || abandonPolicyPending"
              color="error"
              outlined
              class="no-print"
              @click="markPolicyAbandoned"
            >
              {{ abandonPolicyButtonLabel }}
            </v-btn>
            <v-btn
              :disabled="invalid || isMaster || saving"
              :loading="saving"
              color="primary"
              class="no-print"
              @click="save"
            >
              立即提交
            </v-btn>
            <v-btn
              :disabled="isMaster || saving || !hasWhatsappRecipientPhone"
              color="primary"
              outlined
              class="no-print ml-2"
              @click="openOptInWhatsApp"
            >
              發送同意邀請
            </v-btn>
            <v-spacer />

            <v-btn :disabled="saving" color="default" outlined class="no-print" @click="resetForm">
              重新填寫
            </v-btn>

            <!-- Add Update Button -->

            <v-btn v-if="!isNewRecord" color="secondary" outlined @click="openDataItemDialog()">
              <v-icon left small>mdi-plus</v-icon>
              保險批單
            </v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="info"
              outlined
              class="no-print"
              @click="handleDuplicate"
            >
              <v-icon left small>mdi-content-copy</v-icon>
              複製
            </v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="info"
              outlined
              class="no-print"
              @click="handlePrint"
            >
              <v-icon left small>mdi-printer</v-icon>
              列印
            </v-btn>

            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="error"
              outlined
              class="no-print"
              @click="confirmDelete"
            >
              刪除
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>

    <!-- Attachment Preview Dialog -->
    <AttachmentPreviewDialog
      v-model="previewDialog"
      :attachment-id="previewAttachmentId"
      :filename="previewFilename"
      :content-type="previewContentType"
    />

    <!-- WhatsApp attachment selection (after save with auto-forward enabled) -->
    <v-dialog
      v-model="whatsappAttachmentDialog"
      max-width="960px"
      persistent
      scrollable
    >
      <v-card class="whatsapp-attachment-dialog">
        <v-card-title class="text-h6 pb-2">
          選擇要隨 WhatsApp 訊息傳送的附件
        </v-card-title>
        <v-card-subtitle class="pt-1 pb-2">
          傳送對象（{{ whatsappMessageTarget.role }}）：{{ whatsappMessageTarget.name }} · {{ whatsappMessageTarget.phone }}
        </v-card-subtitle>
        <v-divider />
        <v-card-text class="pt-4 pb-2">
          <div class="whatsapp-attachment-dialog-content">
            <div class="whatsapp-attachment-dialog-list">
              <v-alert v-if="!whatsappDialogAttachments.length" type="info" dense outlined class="mb-0">
                {{ whatsappDialogEmptyMessage }}
              </v-alert>
              <template v-else>
                <v-alert
                  :type="isWhatsappTotalSizeLimitReached ? 'error' : 'info'"
                  dense
                  outlined
                  class="mb-3"
                >
                  已選 {{ formatAttachmentSize(selectedWhatsappAttachmentTotalBytes) }} /
                  上限 {{ crmWhatsappAttachmentTotalSizeLimitMb }} MB
                </v-alert>
                <v-alert
                  v-for="(alert, index) in whatsappAttachmentSelectionLimitAlerts"
                  :key="'wa-limit-alert-' + index"
                  :type="alert.type"
                  dense
                  outlined
                  class="mb-3"
                >
                  {{ alert.message }}
                </v-alert>
                <v-list dense class="py-0">
                  <v-list-item
                    v-for="attachment in whatsappDialogAttachments"
                    :key="'wa-att-' + getAttachmentId(attachment)"
                    class="px-0"
                  >
                    <v-list-item-action class="mr-2">
                      <v-checkbox
                        v-model="selectedWhatsappAttachmentIds"
                        :value="getAttachmentId(attachment)"
                        :disabled="isWhatsappAttachmentCheckboxDisabled(attachment)"
                        hide-details
                        class="mt-0"
                      />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>{{ attachment.filename || '文件' }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ formatAttachmentSize(attachment.length) }}
                        <span v-if="getAttachmentLastSentAt(attachment) !== '-'">
                          · 上次傳送：{{ getAttachmentLastSentAt(attachment) }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </template>
            </div>
            <div class="whatsapp-attachment-dialog-stats">
              <div class="whatsapp-attachment-dialog-stats-inner">
                <div class="text-subtitle-2 mb-1">本月附件傳送狀況</div>
                <div class="text-caption grey--text mb-4">
                  {{ whatsappAttachmentSendStatsMonthLabel }}
                </div>
                <v-progress-linear
                  v-if="loadingWhatsappSendStats"
                  indeterminate
                  color="primary"
                  class="mb-3"
                />
                <template v-else>
                  <div class="whatsapp-stat-row">
                    <span class="whatsapp-stat-label">每月附件傳送上限</span>
                    <span class="whatsapp-stat-value">
                      {{ whatsappAttachmentSendStats.maxMonthlyAttachmentSendLimit }}
                    </span>
                  </div>
                  <div class="whatsapp-stat-row">
                    <span class="whatsapp-stat-label">本月已傳送附件數</span>
                    <span class="whatsapp-stat-value">
                      {{ whatsappAttachmentSendStats.sentAttachmentCountThisMonth }}
                    </span>
                  </div>
                  <div class="whatsapp-stat-row">
                    <span class="whatsapp-stat-label">本月剩餘可傳送附件數</span>
                    <span
                      class="whatsapp-stat-value"
                      :class="{
                        'error--text': whatsappAttachmentSendStats.remainingAttachmentCount <= 0,
                        'warning--text':
                          whatsappAttachmentSendStats.remainingAttachmentCount > 0 &&
                          whatsappAttachmentSendStats.remainingAttachmentCount <= 20,
                      }"
                    >
                      {{ whatsappAttachmentSendStats.remainingAttachmentCount }}
                    </span>
                  </div>
                  <v-alert
                    v-if="isWhatsappMonthlyAttachmentLimitReached"
                    type="error"
                    dense
                    outlined
                    class="mb-0 mt-4"
                  >
                    本月附件傳送已達上限，無法再傳送附件。
                  </v-alert>
                  <v-alert
                    v-else-if="isWhatsappMonthlyAttachmentLimitExceededBySelection"
                    type="warning"
                    dense
                    outlined
                    class="mb-0 mt-4"
                  >
                    所選附件數量超過本月剩餘可傳送數量。
                  </v-alert>
                </template>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-spacer />
          <v-btn text :disabled="sendingWhatsapp" @click="handleWhatsappDialogCancel">
            取消
          </v-btn>
          <v-btn
            color="primary"
            :loading="sendingWhatsapp"
            :disabled="isWhatsappSendButtonDisabled"
            @click="sendWhatsappWithSelectedAttachments"
          >
            傳送 WhatsApp 訊息
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Data Item Dialog -->
    <v-dialog v-model="dataItemDialog" max-width="1200px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ dataItemEditIndex === -1 ? '新增' : '編輯' }}保險批單</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDataItemDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <ValidationObserver ref="dataItemForm" v-slot="{ invalid }">
            <v-container>
              <v-row dense>
                <v-col cols="12" md="auto">
                  <ValidationProvider v-slot="{ errors }" name="登記日期" rules="required">
                    <DateInputSeparate
                      v-model="editingDataItem.inputDate"
                      label="登記日期 *"
                      :default-year="currentYear"
                      :error="!!errors.length"
                      :error-messages="errors"
                      :error-details-class="dateFieldErrorDetailsClass"
                      dense
                      flat
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12">
                  <v-subheader class="px-0">信息資料</v-subheader>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="車牌號碼">
                    <v-text-field
                      v-model="editingDataItem.VEHNO"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="車牌號碼"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="廠商">
                    <v-text-field
                      v-model="editingDataItem.MAKE"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="廠商"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="型號">
                    <v-text-field
                      v-model="editingDataItem.MODEL"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="型號"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="年份">
                    <v-text-field
                      v-model="editingDataItem.YEAR"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="年份"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="臨時保單號碼">
                    <v-text-field
                      v-model="editingDataItem.CNNO"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="臨時保單號碼"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="保費">
                    <v-text-field
                      v-model="editingDataItem.PREMIUM"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="保費"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="4" lg="3" xl="2" sm="6">
                  <ValidationProvider v-slot="{ errors }" name="正式保單號碼">
                    <v-text-field
                      v-model="editingDataItem.POLICYNO"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="正式保單號碼"
                      outlined
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12" sm="6" md="6" lg="6">
                  <ValidationProvider v-slot="{ errors }" name="附件" rules="required">
                    <label class="v-label">附件 *</label>
                    <v-radio-group
                      v-model="editingDataItem.depPlace"
                      :error="!!errors.length"
                      :error-messages="errors"
                      row
                      dense
                      hide-details="auto"
                      class="mt-0"
                    >
                      <v-radio
                        v-for="option in depPlaceOptions"
                        :key="option"
                        :value="option"
                        :label="option"
                      ></v-radio>
                    </v-radio-group>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12">
                  <ValidationProvider v-slot="{ errors }" name="備註說明">
                    <v-textarea
                      v-model="editingDataItem.REMARK"
                      :error="!!errors.length"
                      :error-messages="errors"
                      flat
                      label="備註說明"
                      outlined
                      rows="3"
                      dense
                      hide-details="auto"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-container>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDataItemDialog">取消</v-btn>
          <v-btn
            color="primary"
            :loading="savingDataItem"
            :disabled="savingDataItem"
            @click="saveDataItem"
          >
            立即提交
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api';
import { crudRead, crudUpdate } from '@/api/crud';
import { mapGetters } from 'vuex';
import DateInputSeparate from '@/components/DateInputSeparate';
import AttachmentPreviewDialog from '@/components/AttachmentPreviewDialog';
import SearchableCombobox from '@/components/SearchableCombobox';
import { RESTFUL, crmOptionsSubTypes, insuranceTypes, rnTypes } from '@/data/constants';
import { insuranceTypeOptions, needFileOptions } from '@/data/optionsConstants';
import dayjs from 'dayjs';
import ApiError from '@/api/ApiError';
import { uploadSingleAttachment, readAttachment, deleteAttachment } from '@/api/attachment';
import { attnDownloadHref } from '@/services/filters';
import { getOptions } from '@/api/options';
import { cloneDeep, merge } from 'lodash';
import { Validator } from 'vee-validate';

const CRM_END_AFTER_BUSINESS_RULE = 'crm_end_after_business';
if (!Validator.rules[CRM_END_AFTER_BUSINESS_RULE]) {
  Validator.extend(CRM_END_AFTER_BUSINESS_RULE, {
    getMessage: () => '到期日必須晚於起保日期',
    validate(value, [businessDate]) {
      if (!value || !businessDate) return true;
      const end = dayjs(value, 'YYYY-MM-DD').startOf('day');
      const start = dayjs(businessDate, 'YYYY-MM-DD').startOf('day');
      return end.isAfter(start);
    },
  });
}

export default {
  name: 'CRMDetailsForm',
  components: {
    DateInputSeparate,
    AttachmentPreviewDialog,
    SearchableCombobox,
  },
  props: {
    id: {
      type: String,
      default: 'new',
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      docs: null,
      insuranceTypeOptions,
      needFileOptions,
      insuranceCompanyOptions: [],
      agencyOptions: [],
      insPlaceOptions: [],
      depPlaceOptions: [],
      newAttachments: [],
      loading: false,
      saving: false,
      checkingN1: false,
      n1Error: false,
      n1ErrorMessage: '',
      previewDialog: false,
      previewAttachmentId: null,
      previewFilename: '',
      previewContentType: '',
      dataItemDialog: false,
      savingDataItem: false,
      /** True after 放棄續保 click until 立即提交 saves */
      abandonPolicyPending: false,
      /** UI-only: when true, open attachment picker after successful save */
      autoForwardToClient: false,
      whatsappAttachmentDialog: false,
      whatsappSendCrmId: null,
      selectedWhatsappAttachmentIds: [],
      sendingWhatsapp: false,
      loadingWhatsappSendStats: false,
      crmWhatsappAttachmentTotalSizeLimitMb: 60,
      whatsappAttachmentSendStats: {
        maxMonthlyAttachmentSendLimit: 300,
        sentAttachmentCountThisMonth: 0,
        remainingAttachmentCount: 300,
        month: dayjs().format('YYYY-MM'),
      },
      dataItemEditIndex: -1,
      editingDataItem: {
        inputDate: null,
        VEHNO: '',
        MAKE: '',
        MODEL: '',
        YEAR: '',
        CNNO: '',
        PREMIUM: '',
        POLICYNO: '',
        depPlace: '',
        REMARK: '',
      },
      dateFieldErrorDetailsClass: 'mt-3 pt-2',
    };
  },
  async mounted() {
    await this.loadOptions();
    this.reload();
  },
  computed: {
    ...mapGetters('auth', ['role', 'isMaster', 'profileId']),
    isNewRecord() {
      return !this.id || this.id === 'new';
    },
    hasWhatsappRecipientPhone() {
      return !!(this.getTrimmedPhone(this.docs?.TEL) || this.getTrimmedPhone(this.docs?.agentTel));
    },
    autoForwardToClientDisabledReason() {
      return '缺少客戶電話及代理人電話，無法使用自動轉發';
    },
    whatsappMessageTarget() {
      const clientName = (this.docs?.ContactPerson || '').trim() || '-';
      const clientTel = this.getTrimmedPhone(this.docs?.TEL);
      if (clientTel) {
        return { role: '客戶', name: clientName, phone: clientTel };
      }
      const agentName = (this.docs?.agent || '').trim() || '-';
      const agentTel = this.getTrimmedPhone(this.docs?.agentTel) || '-';
      return { role: '代理人', name: agentName, phone: agentTel };
    },
    whatsappDialogAttachments() {
      if (!this.docs?.attachments?.length) {
        return [];
      }
      return this.docs.attachments.filter(
        att => this.getAttachmentId(att) && this.isPdfAttachment(att),
      );
    },
    whatsappDialogEmptyMessage() {
      const attachments = this.docs?.attachments || [];
      const hasAnyAttachment = attachments.some(att => this.getAttachmentId(att));
      if (!hasAnyAttachment) {
        return '此 CRM 尚無附件可傳送。';
      }
      return '此 CRM 尚無 PDF 附件可傳送（僅 PDF 可隨 WhatsApp 訊息傳送）。';
    },
    whatsappAttachmentTotalSizeLimitBytes() {
      return this.crmWhatsappAttachmentTotalSizeLimitMb * 1024 * 1024;
    },
    selectedWhatsappAttachmentTotalBytes() {
      const selectedIds = new Set(this.selectedWhatsappAttachmentIds.map(String));
      return this.whatsappDialogAttachments.reduce((sum, attachment) => {
        const id = String(this.getAttachmentId(attachment));
        if (!selectedIds.has(id)) {
          return sum;
        }
        return sum + (attachment.length || 0);
      }, 0);
    },
    isWhatsappMonthlyAttachmentLimitReached() {
      return this.whatsappAttachmentSendStats.remainingAttachmentCount <= 0;
    },
    isWhatsappTotalSizeLimitReached() {
      return (
        this.selectedWhatsappAttachmentTotalBytes >= this.whatsappAttachmentTotalSizeLimitBytes
      );
    },
    whatsappAttachmentSelectionLimitAlerts() {
      if (!this.whatsappDialogAttachments.length) {
        return [];
      }

      const alerts = [];
      const { sentAttachmentCountThisMonth, maxMonthlyAttachmentSendLimit, remainingAttachmentCount } =
        this.whatsappAttachmentSendStats;

      if (this.isWhatsappMonthlyAttachmentLimitReached) {
        alerts.push({
          type: 'error',
          message:
            `無法再選取更多附件：本月附件傳送已達上限` +
            `（${sentAttachmentCountThisMonth}/${maxMonthlyAttachmentSendLimit}）。`,
        });
      } else if (this.hasWhatsappAttachmentsDisabledDueToMonthlyLimit) {
        alerts.push({
          type: 'error',
          message:
            `部分附件無法選取：本月僅剩 ${remainingAttachmentCount} 個附件名額。`,
        });
      }

      if (this.isWhatsappTotalSizeLimitReached) {
        alerts.push({
          type: 'error',
          message:
            `無法再選取更多附件：已達單次傳送總大小上限` +
            `（${this.crmWhatsappAttachmentTotalSizeLimitMb} MB）。`,
        });
      } else if (this.hasWhatsappAttachmentsDisabledDueToSize) {
        alerts.push({
          type: 'error',
          message:
            `部分附件無法選取：加入後將超過單次傳送總大小上限` +
            `（${this.crmWhatsappAttachmentTotalSizeLimitMb} MB）。`,
        });
      }

      return alerts;
    },
    hasWhatsappAttachmentsDisabledDueToMonthlyLimit() {
      return this.whatsappDialogAttachments.some(attachment =>
        this.isWhatsappAttachmentDisabledDueToMonthlyLimit(attachment),
      );
    },
    hasWhatsappAttachmentsDisabledDueToSize() {
      return this.whatsappDialogAttachments.some(attachment =>
        this.isWhatsappAttachmentDisabledDueToSize(attachment),
      );
    },
    whatsappAttachmentSendStatsMonthLabel() {
      const month = this.whatsappAttachmentSendStats.month;
      if (!month) {
        return dayjs().format('YYYY年M月');
      }
      const parsed = dayjs(`${month}-01`);
      return parsed.isValid() ? parsed.format('YYYY年M月') : month;
    },
    isWhatsappMonthlyAttachmentLimitExceededBySelection() {
      const selectedCount = this.selectedWhatsappAttachmentIds.length;
      if (!selectedCount) {
        return false;
      }
      return (
        this.whatsappAttachmentSendStats.sentAttachmentCountThisMonth +
          selectedCount >
        this.whatsappAttachmentSendStats.maxMonthlyAttachmentSendLimit
      );
    },
    isWhatsappSendButtonDisabled() {
      if (this.sendingWhatsapp || this.loadingWhatsappSendStats) {
        return true;
      }
      if (this.isWhatsappMonthlyAttachmentLimitReached) {
        return true;
      }
      const selectedCount = this.selectedWhatsappAttachmentIds.length;
      if (!selectedCount) {
        return true;
      }
      return this.isWhatsappMonthlyAttachmentLimitExceededBySelection;
    },
    /** Edit only; hide when RNT is Renewed / Cancelled / Abandoned / Sold (except pending abandon) */
    showAbandonPolicyButton() {
      if (this.isNewRecord || !this.docs) return false;
      if (this.abandonPolicyPending) return true;
      const terminalRnt = [
        rnTypes.Renewed,
        rnTypes.Cancelled,
        rnTypes.Abandoned,
        rnTypes.Sold,
      ];
      return !terminalRnt.includes(this.docs.RNT);
    },
    abandonPolicyButtonLabel() {
      return this.abandonPolicyPending ? '放棄續保(未確認)' : '放棄續保';
    },
    currentYear() {
      return dayjs().year();
    },
    inputDate: {
      get() {
        if (!this.docs?.data?.[0]?.inputDate) return null;
        return dayjs(this.docs.data[0].inputDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!this.docs.data?.[0]) return;
        if (!value) {
          this.docs.data[0].inputDate = null;
          return;
        }
        this.docs.data[0].inputDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
      },
    },
    businessDate: {
      get() {
        if (!this.docs?.businessDate) return null;
        return dayjs(this.docs.businessDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!value) {
          this.docs.businessDate = null;
          return;
        }
        this.docs.businessDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
        this.syncEndDateFromBusinessDate(value);
      },
    },
    endDate: {
      get() {
        if (!this.docs?.endDate) return null;
        return dayjs(this.docs.endDate).format('YYYY-MM-DD');
      },
      set(value) {
        if (!value) {
          this.docs.endDate = null;
          return;
        }
        this.docs.endDate = dayjs(value, 'YYYY-MM-DD').startOf('day').toDate();
      },
    },
    endDateRules() {
      return {
        required: true,
        [CRM_END_AFTER_BUSINESS_RULE]: [this.businessDate],
      };
    },
    displayIsSpecialRecord() {
      if (this.docs?.isSpecialRecord === true) return '是';
      if (this.docs?.isSpecialRecord === false) return '否';
      if (!this.docs?.businessDate || !this.docs?.endDate) return '—';
      return this.isNonStandardDateSpan ? '是' : '否';
    },
    isNonStandardDateSpan() {
      if (!this.docs?.businessDate || !this.docs?.endDate) return false;
      const standardEnd = dayjs(this.docs.businessDate)
        .add(1, 'year')
        .subtract(1, 'day')
        .startOf('day');
      return !dayjs(this.docs.endDate).startOf('day').isSame(standardEnd, 'day');
    },
  },
  watch: {
    hasWhatsappRecipientPhone: {
      handler() {
        this.syncAutoForwardToClient();
      },
      immediate: true,
    },
    async businessDate(newVal) {
      if (this.isNewRecord && newVal) {
        await this.generateDefaultN1();
      }
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('YYYY-MM-DD');
    },
    syncEndDateFromBusinessDate(sourceDate) {
      if (!this.docs) return;
      const base = sourceDate
        ? dayjs(sourceDate, 'YYYY-MM-DD').startOf('day')
        : dayjs(this.docs.businessDate).startOf('day');
      if (!base.isValid()) return;
      const endDateStr = base.add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD');
      this.$set(
        this.docs,
        'endDate',
        dayjs(endDateStr, 'YYYY-MM-DD').startOf('day').toDate(),
      );
      this.$nextTick(() => {
        this.$refs.endDateInput?.syncFromParentValue(endDateStr);
      });
    },
    async generateDefaultN1() {
      try {
        if (!this.docs.businessDate) return '';

        const businessDate = dayjs(this.docs.businessDate);

        const response = await api.get(RESTFUL.crm.generateN1, {
          params: {
            businessDate: businessDate.toISOString(),
          },
        });

        const n1 = response.data.n1;
        if (!n1) return;
        this.docs.N1 = n1;
        this.n1Error = false;
        this.n1ErrorMessage = '';
      } catch (e) {
        console.error('Failed to generate N1:', e);
      }
    },
    openDataItemDialog() {
      this.dataItemEditIndex = -1;
      this.editingDataItem = {
        inputDate: dayjs().format('YYYY-MM-DD'),
        VEHNO: '',
        MAKE: '',
        MODEL: '',
        YEAR: '',
        CNNO: '',
        PREMIUM: '',
        POLICYNO: '',
        depPlace: '',
        REMARK: '',
      };
      this.dataItemDialog = true;
    },
    editDataItem(index) {
      this.dataItemEditIndex = index;
      const item = this.docs.data[index];
      this.editingDataItem = {
        inputDate: item.inputDate ? dayjs(item.inputDate).format('YYYY-MM-DD') : null,
        VEHNO: item.VEHNO || '',
        MAKE: item.MAKE || '',
        MODEL: item.MODEL || '',
        YEAR: item.YEAR || '',
        CNNO: item.CNNO || '',
        PREMIUM: item.PREMIUM || '',
        POLICYNO: item.POLICYNO || '',
        depPlace: item.depPlace || '',
        REMARK: item.REMARK || '',
      };
      this.dataItemDialog = true;
    },
    async deleteDataItem(index) {
      const confirmed = await this.$root.$confirm.open('確認刪除', '確定要刪除此保險批單嗎？', {
        color: 'red',
      });
      if (!confirmed) {
        return;
      }

      try {
        const itemId = this.docs.data[index]._id;
        if (itemId) {
          await api.delete(`${RESTFUL.crm.read}/${this.id}/data/${itemId}`);
        }

        // Reload the entire record to get updated data array
        await this.reload();

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    closeDataItemDialog() {
      this.dataItemDialog = false;
      this.dataItemEditIndex = -1;
      this.$refs.dataItemForm?.reset();
    },
    async saveDataItem() {
      this.savingDataItem = true;
      try {
        if (!(await this.$refs.dataItemForm?.validate?.())) {
          this.savingDataItem = false;
          return;
        }

        const payload = {
          inputDate: this.editingDataItem.inputDate
            ? dayjs(this.editingDataItem.inputDate, 'YYYY-MM-DD').startOf('day').toDate()
            : null,
          VEHNO: this.editingDataItem.VEHNO,
          MAKE: this.editingDataItem.MAKE,
          MODEL: this.editingDataItem.MODEL,
          YEAR: this.editingDataItem.YEAR,
          CNNO: this.editingDataItem.CNNO,
          PREMIUM: this.editingDataItem.PREMIUM,
          POLICYNO: this.editingDataItem.POLICYNO,
          depPlace: this.editingDataItem.depPlace,
          REMARK: this.editingDataItem.REMARK,
        };

        if (this.dataItemEditIndex === -1) {
          // Create new data item
          await api.post(`${RESTFUL.crm.read}/${this.id}/data`, payload);
        } else {
          // Update existing data item
          const itemId = this.docs.data[this.dataItemEditIndex]._id;
          await api.patch(`${RESTFUL.crm.read}/${this.id}/data/${itemId}`, payload);
        }

        // Reload the entire record to get updated data array with all items
        await this.reload();

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });

        this.closeDataItemDialog();
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.savingDataItem = false;
      }
    },
    getTrimmedPhone(phone) {
      return typeof phone === 'string' ? phone.trim() : '';
    },
    /** Same rules as CRM list formatDocsRequestPhone. */
    formatOptInInvitePhone(phoneNumber) {
      if (!phoneNumber) {
        return '';
      }
      let digits = String(phoneNumber).replace(/[^\d]/g, '');
      if (!digits.startsWith('852') && digits.length <= 8) {
        digits = '852' + digits;
      }
      return digits;
    },
    async openOptInWhatsApp() {
      const rawPhone =
        this.getTrimmedPhone(this.docs?.TEL) || this.getTrimmedPhone(this.docs?.agentTel);
      const phone = this.formatOptInInvitePhone(rawPhone);
      if (!phone) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '缺少客戶電話及代理人電話，無法發送同意邀請',
          type: 'error',
          color: 'error',
        });
        return;
      }

      let optInLink = '';
      try {
        const { data } = await api.get(RESTFUL.opt.optInMessage);
        optInLink = (data && data.message) || '';
      } catch (e) {
        const ae = new ApiError(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message || '無法取得同意接收訊息',
          type: 'error',
          color: 'error',
        });
        return;
      }
      if (!optInLink) {
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '無法取得同意接收訊息',
          type: 'error',
          color: 'error',
        });
        return;
      }

      const body = this.isNewRecord
        ? '為方便日後向您發送保單文件、保單到期通知及文件補交通知，請按以下連結完成 WhatsApp 登記。'
        : '為方便日後經系統專線向您發送保單服務通知，煩請按以下連結發送同意，謝謝。';
      const message = `您好，我是佑安保險。\n\n${body}\n\n👉 ${optInLink}`;
      const url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
        message,
      )}`;
      window.open(url, '_blank');
    },
    handleCustomerSelect(customer) {
      // Only update related phone field when user clicks an item from dropdown
      if (customer && typeof customer === 'object') {
        this.docs.ContactPerson = customer.name || '';
        this.docs.TEL = customer.phone || '';
      }
    },
    handleCustomerPhoneSelect(profile) {
      // Only update related name field when user clicks an item from dropdown
      if (profile && typeof profile === 'object') {
        this.docs.TEL = profile.phone || '';
        this.docs.ContactPerson = profile.name || '';
      }
    },
    handleAgentSelect(agent) {
      // Only update related phone field when user clicks an item from dropdown
      if (agent && typeof agent === 'object') {
        this.docs.agent = agent.name || '';
        this.docs.agentTel = agent.phone || '';
      }
    },
    handleAgentPhoneSelect(profile) {
      // Only update related name field when user clicks an item from dropdown
      if (profile && typeof profile === 'object') {
        this.docs.agentTel = profile.phone || '';
        this.docs.agent = profile.name || '';
      }
    },
    async checkN1Duplicate() {
      // Reset error state
      this.n1Error = false;
      this.n1ErrorMessage = '';

      // Skip if N1 is empty
      if (!this.docs.N1) {
        return;
      }

      this.checkingN1 = true;
      try {
        const response = await api.get(RESTFUL.crm.list, {
          params: {
            filter: { N1: this.docs.N1 },
            limit: 1,
          },
        });

        const existingRecords = response.data.docs || response.data;

        // Check if duplicate exists
        // If editing, allow current record's N1
        if (existingRecords.length > 0) {
          const isDuplicate = existingRecords.some(
            record => record._id !== this.id && record.N1 === this.docs.N1,
          );

          if (isDuplicate) {
            this.n1Error = true;
            this.n1ErrorMessage = '此檔案編號已存在，請使用其他編號';
          }
        }
      } catch (e) {
        console.error('Failed to check N1 duplicate:', e);
      } finally {
        this.checkingN1 = false;
      }
    },
    async loadOptions() {
      this.loading = true;
      try {
        // Load all CRM-related options in a single API call
        const allOptions = await getOptions(api, {
          filter: { type: 'crm' },
          sort: { order: 1, createdAt: -1 },
        });

        const options = allOptions.docs || allOptions;

        // Group options by subType
        this.insuranceCompanyOptions = options
          .filter(opt => opt.subType === crmOptionsSubTypes.insuranceCompany)
          .map(opt => opt.name);

        this.agencyOptions = options
          .filter(opt => opt.subType === crmOptionsSubTypes.agency)
          .map(opt => opt.name);

        this.insPlaceOptions = options
          .filter(opt => opt.subType === crmOptionsSubTypes.insuredPlace)
          .map(opt => opt.name);

        this.depPlaceOptions = options
          .filter(opt => opt.subType === crmOptionsSubTypes.fileStoragePlace)
          .map(opt => opt.name);

        try {
          const res = await api.get(
            RESTFUL.systemConfig.getWhatsappAttachmentTotalSizeLimitMb,
          );
          const limit = Number(res.data?.value);
          if (!Number.isNaN(limit) && limit > 0) {
            this.crmWhatsappAttachmentTotalSizeLimitMb = limit;
          }
        } catch (e) {
          this.crmWhatsappAttachmentTotalSizeLimitMb = 60;
        }
      } catch (e) {
        console.error('Failed to load options:', e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '載入選項失敗',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async reload() {
      this.abandonPolicyPending = false;
      if (this.isNewRecord) {
        // Initialize new record with default values or initialData
        const now = dayjs().startOf('day');

        // Default new record
        this.docs = {
          businessDate: null,
          endDate: null,
          isSpecialRecord: false,
          insPlace: this.insPlaceOptions[0] || '',
          insType: insuranceTypes.ThirdParty,
          needFile: [],
          attachments: [],
          N1: '',
          data: [
            {
              inputDate: now.toDate(),
              depPlace: '',
              VEHNO: '',
              MAKE: '',
              MODEL: '',
              YEAR: '',
              CNNO: '',
              PREMIUM: '',
              POLICYNO: '',
              REMARK: '',
            },
          ],
        };
        if (this.initialData) {
          this.docs = merge(this.docs, this.initialData);
        }
      } else {
        this.docs = await crudRead(api, RESTFUL.crm.read, this.id, {
          populates: [
            { path: 'attachments', select: ['filename', '_id', 'contentType', 'length'] },
          ],
        });
        // NCD : Remove % from D1 if it exists
        this.docs.D1 =
          typeof this.docs.D1 === 'string' && this.docs.D1.endsWith('%')
            ? this.docs.D1.slice(0, -1)
            : this.docs.D1;

        // Ensure needFile is an array
        if (!this.docs.needFile) {
          this.docs.needFile = [];
        }
        // Ensure attachments is an array
        if (!this.docs.attachments) {
          this.docs.attachments = [];
        }
        // Ensure data array exists and has at least one item
        if (!this.docs.data || !Array.isArray(this.docs.data)) {
          this.docs.data = [];
        }
        if (this.docs.data.length === 0) {
          this.docs.data.push({
            inputDate: dayjs().startOf('day').toDate(),
            depPlace: '',
            VEHNO: '',
            MAKE: '',
            MODEL: '',
            YEAR: '',
            CNNO: '',
            PREMIUM: '',
            POLICYNO: '',
            REMARK: '',
          });
        }
      }
      this.syncAutoForwardToClient();
    },
    syncAutoForwardToClient() {
        if (!this.hasWhatsappRecipientPhone) {
          this.autoForwardToClient = false;
        }
    },
    async handleAttachmentSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length) {
        this.newAttachments = files;
        await this.uploadAttachments();
        event.target.value = ''; // Reset input
      }
    },
    async uploadAttachments() {
      if (!this.newAttachments.length) {
        return;
      }

      try {
        const attachments = await Promise.all(
          this.newAttachments.map(file => uploadSingleAttachment(api, file, this.profileId)),
        );

        // Add new attachments (full objects) to the list
        const currentAttachments = Array.isArray(this.docs.attachments)
          ? this.docs.attachments
          : [];

        this.docs.attachments = [...currentAttachments, ...attachments];
        this.newAttachments = [];

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '附件已添加，請點擊儲存以保存變更',
          type: 'info',
          color: 'info',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async removeAttachment(index) {
      const confirmed = await this.$root.$confirm.open('確認刪除', '確定要刪除此附件嗎？', {
        color: 'red',
      });
      if (!confirmed) {
        return;
      }

      try {
        // Just remove from the list, will be saved when user clicks save
        this.docs.attachments.splice(index, 1);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '附件已移除，請點擊儲存以保存變更',
          type: 'info',
          color: 'info',
        });
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    getAttachmentId(attachment) {
      if (!attachment) return '';
      return typeof attachment === 'object' ? String(attachment._id || '') : String(attachment);
    },
    getAttachmentLastSentAt(attachment) {
      const id = this.getAttachmentId(attachment);
      if (!id || !this.docs?.attachmentSentWithMessageAt) {
        return '-';
      }
      const history = this.docs.attachmentSentWithMessageAt[id];
      if (!Array.isArray(history) || history.length === 0) {
        return '-';
      }
      const last = history[history.length - 1];
      return dayjs(last).isValid() ? dayjs(last).format('YYYY-MM-DD HH:mm') : '-';
    },
    formatAttachmentSize(bytes) {
      const size = Number(bytes) || 0;
      if (size <= 0) {
        return '-';
      }
      if (size < 1024) {
        return `${size} B`;
      }
      if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(1)} KB`;
      }
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },
    isPdfAttachment(attachment) {
      const contentType = attachment?.contentType || '';
      const filename = attachment?.filename || '';
      return contentType.includes('pdf') || filename.toLowerCase().endsWith('.pdf');
    },
    isWhatsappAttachmentDisabledDueToMonthlyLimit(attachment) {
      const id = String(this.getAttachmentId(attachment));
      if (this.selectedWhatsappAttachmentIds.map(String).includes(id)) {
        return false;
      }
      if (this.isWhatsappMonthlyAttachmentLimitReached) {
        return true;
      }
      const selectedCount = this.selectedWhatsappAttachmentIds.length;
      const { sentAttachmentCountThisMonth, maxMonthlyAttachmentSendLimit } =
        this.whatsappAttachmentSendStats;
      return (
        sentAttachmentCountThisMonth + selectedCount + 1 > maxMonthlyAttachmentSendLimit
      );
    },
    isWhatsappAttachmentDisabledDueToSize(attachment) {
      const id = String(this.getAttachmentId(attachment));
      if (this.selectedWhatsappAttachmentIds.map(String).includes(id)) {
        return false;
      }
      const attachmentSize = attachment.length || 0;
      return (
        this.selectedWhatsappAttachmentTotalBytes + attachmentSize >
        this.whatsappAttachmentTotalSizeLimitBytes
      );
    },
    isWhatsappAttachmentCheckboxDisabled(attachment) {
      return (
        this.isWhatsappAttachmentDisabledDueToMonthlyLimit(attachment) ||
        this.isWhatsappAttachmentDisabledDueToSize(attachment)
      );
    },
    async loadWhatsappAttachmentSendStats() {
      this.loadingWhatsappSendStats = true;
      try {
        const { data } = await api.get(
          `${RESTFUL.crm.read}/whatsapp-attachment-send-stats`,
        );
        if (data) {
          this.whatsappAttachmentSendStats = {
            maxMonthlyAttachmentSendLimit:
              data.maxMonthlyAttachmentSendLimit != null
                ? data.maxMonthlyAttachmentSendLimit
                : 300,
            sentAttachmentCountThisMonth:
              data.sentAttachmentCountThisMonth != null
                ? data.sentAttachmentCountThisMonth
                : 0,
            remainingAttachmentCount:
              data.remainingAttachmentCount != null
                ? data.remainingAttachmentCount
                : 0,
            month: data.month || dayjs().format('YYYY-MM'),
          };
        }
      } catch (e) {
        console.error('Failed to load WhatsApp attachment send stats:', e);
        this.whatsappAttachmentSendStats.month = dayjs().format('YYYY-MM');
      } finally {
        this.loadingWhatsappSendStats = false;
      }
    },
    openWhatsappAttachmentDialog(crmId) {
      this.whatsappSendCrmId = crmId;
      this.selectedWhatsappAttachmentIds = [];
      this.whatsappAttachmentDialog = true;
      this.loadWhatsappAttachmentSendStats();
    },
    closeWhatsappAttachmentDialog() {
      this.whatsappAttachmentDialog = false;
      this.whatsappSendCrmId = null;
      this.selectedWhatsappAttachmentIds = [];
    },
    cancelWhatsappAttachmentDialog() {
      this.closeWhatsappAttachmentDialog();
    },
    handleWhatsappDialogCancel() {
      this.cancelWhatsappAttachmentDialog();
      this.completeSaveAfterWhatsappFlow();
    },
    completeSaveAfterWhatsappFlow() {
      this.$emit('saved');
    },
    async sendWhatsappWithSelectedAttachments() {
      const crmId = this.whatsappSendCrmId || this.id;
      if (!crmId || crmId === 'new') {
        return;
      }
      this.sendingWhatsapp = true;
      try {
        const { phone } = this.whatsappMessageTarget;
        const payload = {
          attachmentIds: this.selectedWhatsappAttachmentIds,
        };
        if (phone && phone !== '-') {
          payload.phone = phone;
        }
        const { data } = await api.post(
          `${RESTFUL.crm.read}/${crmId}/send-whatsapp-message`,
          payload,
        );
        const success = data?.success !== false;
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: data?.message || (success ? 'WhatsApp 訊息已傳送' : '傳送失敗'),
          type: success ? 'success' : 'error',
          color: success ? 'success' : 'error',
        });
        if (success) {
          if (!this.isNewRecord && crmId === this.id) {
            await this.reload();
          } else if (this.isNewRecord && crmId) {
            this.docs = await crudRead(api, RESTFUL.crm.read, crmId, {
              populates: [
                { path: 'attachments', select: ['filename', '_id', 'contentType', 'length'] },
              ],
            });
          }
          await this.loadWhatsappAttachmentSendStats();
          this.closeWhatsappAttachmentDialog();
          this.completeSaveAfterWhatsappFlow();
        }
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.sendingWhatsapp = false;
      }
    },
    downloadAttachment(attachment) {
      const attachmentId = typeof attachment === 'object' ? attachment._id : attachment;
      const filename = typeof attachment === 'object' ? attachment.filename : '';
      if (attachmentId) {
        // Open all files in viewer page (new tab)
        const route = this.$router.resolve({
          name: 'FileViewerPage',
          params: { attachmentId },
          query: { filename: filename || '文件' },
        });
        window.open(route.href, '_blank');
      }
    },
    resetForm() {
      // Reset form validation
      this.$refs.form?.reset();
      // Reload data
      this.reload();
    },
    handleDuplicate() {
      // Emit the whole docs object, let parent handle field selection
      this.$emit('duplicate', this.docs);
    },
    handlePrint() {
      const container = this.$refs.formContainer;
      if (!container) return;

      // 1. Clone the form content deeply
      const clonedContent = container.cloneNode(true);

      // 2. Create a print wrapper container
      const printWrapper = document.createElement('div');
      printWrapper.className = 'v-application print-wrapper';
      printWrapper.appendChild(clonedContent);

      // 3. Copy computed styles to ensure accurate rendering
      // this.copyComputedStyles(container, clonedContent);

      // 4. Add printing mode class to body to hide other elements
      document.body.classList.add('printing-mode');

      // 5. Append print wrapper to body
      document.body.appendChild(printWrapper);

      // 6. Cleanup after print
      const afterPrint = () => {
        if (document.body.contains(printWrapper)) {
          document.body.removeChild(printWrapper);
        }
        document.body.classList.remove('printing-mode');
        window.removeEventListener('afterprint', afterPrint);
      };

      window.addEventListener('afterprint', afterPrint);

      // 7. Trigger print with a slight delay to ensure DOM is ready
      setTimeout(() => {
        window.print();
      }, 200);
    },

    // Helper method to copy important computed styles
    copyComputedStyles(source, target) {
      // Get all elements from both source and target
      const sourceElements = source.getElementsByTagName('*');
      const targetElements = target.getElementsByTagName('*');

      // Copy styles for container
      const sourceStyles = window.getComputedStyle(source);
      const importantStyles = [
        'display',
        'width',
        'height',
        'padding',
        'margin',
        'font-family',
        'font-size',
        'color',
        'background-color',
      ];

      importantStyles.forEach(prop => {
        target.style[prop] = sourceStyles[prop];
      });

      // Copy styles for all child elements
      for (let i = 0; i < sourceElements.length && i < targetElements.length; i++) {
        const srcStyles = window.getComputedStyle(sourceElements[i]);
        importantStyles.forEach(prop => {
          targetElements[i].style[prop] = srcStyles[prop];
        });
      }
    },
    async confirmDelete() {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此記錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
      );
      if (!confirmed) {
        return;
      }
      this.deleteRecord();
    },
    async deleteRecord() {
      try {
        await api.delete(`${RESTFUL.crm.read}/${this.id}`);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });

        // Emit deleted event
        this.$emit('deleted');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    markPolicyAbandoned() {
      if (!this.docs || this.abandonPolicyPending) return;
      this.docs.RNT = rnTypes.Abandoned;
      this.abandonPolicyPending = true;
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        // Prepare payload with the new nested structure
        // Include all data items to preserve added/updated 保險批單
        const payload = {
          insPlace: this.docs.insPlace,
          ContactPerson: this.docs.ContactPerson,
          insType: this.docs.insType,
          N1: this.docs.N1,
          NAMEOFINS: this.docs.NAMEOFINS,
          D1: this.docs.D1,
          AGENCY: this.docs.AGENCY,
          TEL: this.docs.TEL,
          agent: this.docs.agent,
          agentTel: this.docs.agentTel,
          businessDate: this.docs.businessDate,
          endDate: this.docs.endDate,
          needFile: this.docs.needFile,
          ...(this.docs.RNT ? { RNT: this.docs.RNT } : {}),
          attachments: this.docs.attachments
            ? this.docs.attachments.map(att => (typeof att === 'object' ? att._id : att))
            : [],
          data: this.docs.data.map(item => ({
            _id: item._id,
            inputDate: item.inputDate,
            VEHNO: item.VEHNO,
            MAKE: item.MAKE,
            MODEL: item.MODEL,
            YEAR: item.YEAR,
            CNNO: item.CNNO,
            PREMIUM: item.PREMIUM,
            POLICYNO: item.POLICYNO,
            depPlace: item.depPlace,
            REMARK: item.REMARK,
          })),
        };

        const shouldOpenWhatsappDialog =
          this.autoForwardToClient && this.hasWhatsappRecipientPhone;
        let savedCrmId = this.id;

        if (this.isNewRecord) {
          if (this.docs.duplicatedFromCrmId) {
            payload.duplicatedFromCrmId = this.docs.duplicatedFromCrmId;
          }
          const { data: created } = await api.post(RESTFUL.crm.create, payload);
          savedCrmId = created?._id || created?.id || savedCrmId;
        } else {
          await crudUpdate(api, RESTFUL.crm.read, this.id, payload);
        }

        this.abandonPolicyPending = false;

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });

        if (shouldOpenWhatsappDialog && savedCrmId && savedCrmId !== 'new') {
          if (!this.isNewRecord) {
            await this.reload();
          }
          this.openWhatsappAttachmentDialog(savedCrmId);
        } else {
          this.$emit('saved');
        }
      } catch (e) {
        if (e.response?.data?.message === 'N1 already exists') {
          const confirmed = await this.$root.$confirm.open(
            '檔案編號重複',
            '此檔案編號已存在，是否要自動生成一個新的編號？',
            {
              color: 'warning',
            },
          );

          if (confirmed) {
            await this.generateDefaultN1();
          }
          return;
        }
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.whatsapp-attachment-dialog-content {
  display: flex;
  flex-direction: column;
}

.whatsapp-attachment-dialog-list {
  min-width: 0;
}

.whatsapp-attachment-dialog-stats-inner {
  height: 100%;
}

.whatsapp-stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.whatsapp-stat-row:last-of-type {
  border-bottom: none;
}

.whatsapp-stat-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  padding-right: 12px;
}

.whatsapp-stat-value {
  font-size: 16px;
  font-weight: 500;
  flex-shrink: 0;
}

@media (min-width: 960px) {
  .whatsapp-attachment-dialog-content {
    flex-direction: row;
    align-items: stretch;
    min-height: 260px;
  }

  .whatsapp-attachment-dialog-list {
    flex: 1 1 58%;
    padding-right: 20px;
    max-height: 360px;
    overflow-y: auto;
  }

  .whatsapp-attachment-dialog-stats {
    flex: 0 0 42%;
    max-width: 360px;
    border-left: 1px solid rgba(0, 0, 0, 0.12);
    padding-left: 20px;
  }

  .whatsapp-attachment-dialog-stats-inner {
    background: #fafafa;
    border-radius: 4px;
    padding: 16px;
  }
}

@media (max-width: 959px) {
  .whatsapp-attachment-dialog-stats {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  .whatsapp-attachment-dialog-stats-inner {
    background: #fafafa;
    border-radius: 4px;
    padding: 16px;
  }
}
</style>
